import axios from "axios";
import { Address, TonClient } from "ton";

const MAX_RATE_LIMIT_RETRIES = 3;
const MAX_RETRY_DELAY_MS = 30000;

export function getTonRpcErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.status === 429) {
    return "节点请求过于频繁，请稍后重试";
  }
  return error instanceof Error ? error.message : "加载代币信息失败，请重试";
}

function retryAfterMs(error: unknown): number {
  if (!axios.isAxiosError(error)) return 0;
  const value = error.response?.headers?.["retry-after"];
  if (!value) return 0;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : 0;
}

export class TonRpcClient extends TonClient {
  private queue: Promise<void> = Promise.resolve();
  private nextRequestAt = 0;

  constructor(
    parameters: ConstructorParameters<typeof TonClient>[0],
    private readonly requestIntervalMs: number,
  ) {
    super(parameters);
  }

  private enqueueRead<T>(read: () => Promise<T>): Promise<T> {
    const result = this.queue.then(async () => {
      for (let attempt = 0; ; attempt++) {
        const wait = this.nextRequestAt - Date.now();
        if (wait > 0) {
          await new Promise((resolve) => setTimeout(resolve, wait));
        }
        this.nextRequestAt = Date.now() + this.requestIntervalMs;
        try {
          return await read();
        } catch (error) {
          if (!axios.isAxiosError(error) || error.response?.status !== 429) throw error;
          const delay = Math.max(2000 * 2 ** attempt, retryAfterMs(error));
          this.nextRequestAt = Math.max(this.nextRequestAt, Date.now() + delay);
          if (attempt >= MAX_RATE_LIMIT_RETRIES || delay > MAX_RETRY_DELAY_MS) throw error;
        }
      }
    });
    this.queue = result.then(
      () => {},
      () => {},
    );
    return result;
  }

  getContractState(address: Address) {
    return this.enqueueRead(() => super.getContractState(address));
  }

  callGetMethod(...args: Parameters<TonClient["callGetMethod"]>) {
    return this.enqueueRead(() => super.callGetMethod(...args));
  }

  callGetMethodWithError(...args: Parameters<TonClient["callGetMethodWithError"]>) {
    return this.enqueueRead(() => super.callGetMethodWithError(...args));
  }
}
